/* eslint-disable max-lines */
import {
  useAdminTenants,
  useCreateTenant,
} from '@hakimo-ui/hakimo/data-access';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import { toast, useCanCreateTenant } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { defaultFilters, TenantsFilters } from '../types';
import { CreateTenantCard } from './CreateTenantCard';
import TenantCard from './TenantCard';
import FilterPanel from './filter-panel/FilterPanel';
import TenantsHeader from './TenantsHeader';
import { TenantCreationLoadingModal } from './TenantCreationLoadingModal';
import {
  getTableData,
  getSearchParams,
  getUrlSearchParams,
  PAGE_SIZE,
} from './util';

export function TenantsList() {
  const navigate = useNavigate();
  const canCreateTenant = useCanCreateTenant();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<TenantsFilters>(defaultFilters);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [tableData, setTableData] = useState<TableData>({
    columns: [],
    rows: [],
  });
  const [isCreating, setIsCreating] = useState(false);
  const [newTenantId, setNewTenantId] = useState('');
  const [newTenantName, setNewTenantName] = useState('');
  const [newSourceSystem, setNewSourceSystem] = useState<
    'Motion' | 'Vision' | undefined
  >(undefined);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const searchParams = useMemo(
    () => getSearchParams(searchValue, filters, page, PAGE_SIZE),
    [searchValue, filters, page]
  );

  const tenantsQuery = useAdminTenants(searchParams);
  const { data } = tenantsQuery;

  useEffect(() => {
    const tenantName = urlSearchParams.get('tenantName') || '';
    const locationCity = urlSearchParams.get('locationCity') || '';
    const locationState = urlSearchParams.get('locationState') || '';
    const locationCountry = urlSearchParams.get('locationCountry') || '';
    const showInactiveTenants =
      urlSearchParams.get('showInactiveTenants') === 'true';
    const productType = urlSearchParams.get('productType') || '';
    const pageParam = urlSearchParams.get('page');
    const viewModeParam = urlSearchParams.get('viewMode') as
      | 'grid'
      | 'list'
      | null;

    if (tenantName) setSearchValue(tenantName);
    if (viewModeParam) setViewMode(viewModeParam);
    if (pageParam) setPage(Number(pageParam));

    if (
      locationCity ||
      locationState ||
      locationCountry ||
      showInactiveTenants ||
      productType
    ) {
      setFilters({
        tenantName: '',
        locationCity,
        locationState,
        locationCountry,
        showInactiveTenants,
        productType,
      });
    }
  }, []);

  useEffect(() => {
    const params = getUrlSearchParams(searchValue, filters, page, viewMode);

    if (params.toString() !== urlSearchParams.toString()) {
      setUrlSearchParams(params, { replace: true });
    }
  }, [
    searchValue,
    filters,
    page,
    viewMode,
    urlSearchParams,
    setUrlSearchParams,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchValue, filters]);

  const createTenantMutation = useCreateTenant(
    (response) => {
      toast(
        'Tenant created successfully. Add new locations for this tenant in portal.hakimo.ai',
        { type: 'success' }
      );
      const tenantId = response.payload.tenant.id;
      navigate(`/tenants-admin/${tenantId}`);
    },
    (error) => {
      toast(error.message || 'Failed to create tenant', { type: 'error' });
    }
  );

  const onClickRow = useCallback(
    (tenantId: string, event: React.MouseEvent | React.KeyboardEvent) => {
      const url = `/tenants-admin/${tenantId}`;
      const isNewTab = event.ctrlKey || event.metaKey;
      if (isNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (data?.items) {
      setTableData(getTableData(data.items, onClickRow));
      setTotal(data.total);
    }
  }, [data, onClickRow]);

  const fromIndex = (page - 1) * PAGE_SIZE;
  const toIndex = Math.min(fromIndex + PAGE_SIZE - 1, total - 1);

  const hasFiltersApplied = useMemo(() => {
    return (
      filters.tenantName.length > 0 ||
      filters.locationCity.length > 0 ||
      filters.locationState.length > 0 ||
      filters.locationCountry.length > 0 ||
      filters.showInactiveTenants ||
      filters.productType.length > 0
    );
  }, [filters]);

  const handleApplyFilters = (newFilters: TenantsFilters) => {
    setFilters(newFilters);
    setOpenFilterPanel(false);
  };

  const resetFormState = () => {
    setNewTenantId('');
    setNewTenantName('');
    setNewSourceSystem(undefined);
  };

  const handleClickCreate = () => {
    setIsCreating(true);
    resetFormState();
  };

  const handleClickCancel = () => {
    setIsCreating(false);
    resetFormState();
  };

  const handleClickSave = () => {
    if (!newSourceSystem) return;
    createTenantMutation.mutate({
      tenantId: newTenantId,
      tenantName: newTenantName,
      sourceSystem: newSourceSystem,
      createSupportUser: true,
    });
  };

  const isSaveDisabled =
    !newTenantId ||
    !newTenantName ||
    !newSourceSystem ||
    !/^[a-z0-9-]+$/.test(newTenantId) ||
    !/^[a-zA-Z0-9]+$/.test(newTenantName) ||
    createTenantMutation.isLoading;

  const tableFooter = (
    <div className="dark:bg-dark-bg border-t bg-white pt-4 dark:border-gray-800">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={PAGE_SIZE}
        total={total}
        disabled={tenantsQuery.isLoading}
        onChangePage={setPage}
      />
    </div>
  );

  const pageActions = useMemo(() => {
    if (isCreating) {
      return [
        <Button
          key="save"
          variant="primary"
          onClick={handleClickSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>,
        <Button key="cancel" onClick={handleClickCancel}>
          Cancel
        </Button>,
      ];
    }
    return canCreateTenant
      ? [
          <Button key="create" variant="primary" onClick={handleClickCreate}>
            <PlusIcon className="mr-1 h-4 w-4" />
            Create
          </Button>,
        ]
      : [];
  }, [
    isCreating,
    handleClickSave,
    isSaveDisabled,
    handleClickCancel,
    handleClickCreate,
    canCreateTenant,
  ]);

  return (
    <Page
      title={isCreating ? 'New' : 'Tenants'}
      subtitle={isCreating ? 'Tenant' : undefined}
      titleFontSize="large"
      pageActions={pageActions}
      onClickBack={isCreating ? handleClickCancel : undefined}
    >
      {isCreating ? (
        <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg h-full px-4 py-8">
          <div className="mx-auto w-full max-w-6xl">
            <CreateTenantCard
              mode="create"
              tenantId={newTenantId}
              tenantName={newTenantName}
              sourceSystem={newSourceSystem}
              onTenantIdChange={setNewTenantId}
              onTenantNameChange={setNewTenantName}
              onSourceSystemChange={setNewSourceSystem}
            />
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col px-4 py-4">
          <TenantsHeader
            searchValue={searchValue}
            onSearchChange={(e) => setSearchValue(e.target.value)}
            hasFiltersApplied={hasFiltersApplied}
            onClickFilter={() => setOpenFilterPanel(true)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          {viewMode === 'grid' ? (
            <div className="mt-4 flex flex-1 flex-col">
              <div className="flex-1 overflow-y-auto">
                <QueryResult queryResult={tenantsQuery}>
                  {(response) => (
                    <div className="grid grid-cols-4 gap-4">
                      {response.items.map((tenant) => (
                        <div key={tenant.id} className="max-w-full">
                          <TenantCard tenant={tenant} />
                        </div>
                      ))}
                    </div>
                  )}
                </QueryResult>
              </div>
              {total > 0 && tableFooter}
            </div>
          ) : (
            <div className="dark:bg-dark-bg mt-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden rounded-t-xl">
              <Table
                data={tableData}
                loading={tenantsQuery.isLoading}
                footer={tableFooter}
              />
            </div>
          )}
        </div>
      )}
      <FilterPanel
        open={openFilterPanel}
        filters={filters}
        onClose={() => setOpenFilterPanel(false)}
        onApplyFilters={handleApplyFilters}
      />
      <TenantCreationLoadingModal open={createTenantMutation.isLoading} />
    </Page>
  );
}

export default TenantsList;
